import { useEffect, useRef } from 'react';

import { t } from '../../../lib/i18n';
import { cn } from '../utils';

export type ClientTab = 'verification' | 'bids' | 'won' | 'finances' | 'visits' | 'personal';

export const CLIENT_TABS: ClientTab[] = ['verification', 'bids', 'won', 'finances', 'visits', 'personal'];

/**
 * «Верифікація · Ставки · Виграні · Фінанси · Відвідування · Дані» — the five
 * tabs both artboards draw over the client, plus «Відвідування» (admin
 * analytics, phase 5: the client's visit history). Segmented on desktop, a
 * scrolling pill row on mobile, because six Ukrainian labels do not fit 390px
 * in one line.
 */
export function ClientDetailTabs({
  active,
  onChange,
  tabs = CLIENT_TABS,
}: {
  active: ClientTab;
  onChange: (tab: ClientTab) => void;
  /** The tabs this operator may open — «Відвідування» is superuser only. */
  tabs?: readonly ClientTab[];
}) {
  // The mobile pill row scrolls sideways; a tab opened from the URL
  // (`?tab=visits`, the sixth) would otherwise sit past the right edge.
  const activePill = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const pill = activePill.current;
    // jsdom has no scrollIntoView; a hidden (desktop) row makes it a no-op.
    if (pill && typeof pill.scrollIntoView === 'function') {
      pill.scrollIntoView({ inline: 'nearest', block: 'nearest' });
    }
  }, [active]);

  return (
    <>
      <div className="-mx-4 flex gap-1.5 overflow-x-auto px-4 lg:hidden">
        {tabs.map((tab) => (
          <button
            key={tab}
            ref={active === tab ? activePill : undefined}
            type="button"
            onClick={() => onChange(tab)}
            className={cn(
              'h-9 shrink-0 rounded-full border px-3 text-[13px] font-semibold whitespace-nowrap',
              active === tab
                ? 'border-cf-brand bg-cf-brand text-white'
                : 'border-cf-line bg-cf-surface text-cf-ink',
            )}
          >
            {t(`clients.detail.tabs.${tab}`)}
          </button>
        ))}
      </div>

      <div role="tablist" className="hidden w-fit gap-0.5 rounded-cf-lg bg-cf-fill p-[3px] lg:flex">
        {tabs.map((tab) => (
          <button
            key={tab}
            type="button"
            role="tab"
            aria-selected={active === tab}
            onClick={() => onChange(tab)}
            className={cn(
              'h-9 rounded-cf-md px-3.5 text-[13px] font-semibold whitespace-nowrap',
              active === tab ? 'bg-cf-surface text-cf-ink' : 'bg-transparent text-cf-ink-muted',
            )}
          >
            {t(`clients.detail.tabs.${tab === 'personal' ? 'personalLong' : tab}`)}
          </button>
        ))}
      </div>
    </>
  );
}
